import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { DropdownMenu as M, Select as S } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatKeys } from '@/lib/shortcuts';

const CONTENT =
  'z-50 min-w-44 animate-pop rounded-lg border border-border bg-surface p-1 text-sm text-fg shadow-pop outline-none';
const ITEM =
  'relative flex h-7.5 cursor-pointer items-center gap-2 rounded-md px-2 text-sm outline-none select-none ' +
  'data-[highlighted]:bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ' +
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-muted';

// ---------- Dropdown menu ----------

export const DropdownMenu = M.Root;
export const DropdownMenuTrigger = M.Trigger;
export const DropdownMenuGroup = M.Group;
export const DropdownMenuSub = M.Sub;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  align = 'start',
  ...props
}: ComponentProps<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(
          CONTENT,
          'max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto',
          className,
        )}
        {...props}
      />
    </M.Portal>
  );
}

export function DropdownMenuItem({
  className,
  danger,
  keys,
  children,
  ...props
}: ComponentProps<typeof M.Item> & { danger?: boolean; keys?: string }) {
  return (
    <M.Item className={cn(ITEM, danger && 'text-danger [&_svg]:text-danger', className)} {...props}>
      {children}
      {keys ? (
        <span className="ml-auto pl-4 text-xs text-fg-subtle">
          {formatKeys(keys)
            .map((c) => c.join(''))
            .join(' ')}
        </span>
      ) : null}
    </M.Item>
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof M.CheckboxItem>) {
  return (
    <M.CheckboxItem className={cn(ITEM, className)} {...props}>
      {children}
      <M.ItemIndicator className="ml-auto">
        <Check className="text-accent-text!" />
      </M.ItemIndicator>
    </M.CheckboxItem>
  );
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof M.Label>) {
  return <M.Label className={cn('px-2 py-1 text-xs font-medium text-fg-subtle', className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: ComponentProps<typeof M.Separator>) {
  return <M.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
}

export function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof M.SubTrigger>) {
  return (
    <M.SubTrigger className={cn(ITEM, 'data-[state=open]:bg-hover', className)} {...props}>
      {children}
      <ChevronRight className="ml-auto" />
    </M.SubTrigger>
  );
}

export function DropdownMenuSubContent({ className, ...props }: ComponentProps<typeof M.SubContent>) {
  return (
    <M.Portal>
      <M.SubContent sideOffset={4} collisionPadding={8} className={cn(CONTENT, className)} {...props} />
    </M.Portal>
  );
}

// ---------- Select ----------

export interface SelectOption<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

/** Simple single-value select. For searchable or multi-select pickers, compose Popover instead. */
export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder = 'Select',
  className,
  disabled,
  size = 'md',
  'aria-label': ariaLabel,
  onCloseAutoFocus,
}: {
  value: T | undefined;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  'aria-label'?: string;
  /** Runs as the menu closes; call `preventDefault` to keep focus from returning to the trigger. */
  onCloseAutoFocus?: (event: Event) => void;
}) {
  return (
    <S.Root value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
      <S.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex items-center justify-between gap-2 rounded-md border border-border-strong bg-surface text-sm text-fg',
          'hover:bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-60',
          'data-[placeholder]:text-fg-subtle',
          size === 'sm' ? 'h-7 px-2' : 'h-8 px-2.5',
          className,
        )}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-3.5 text-fg-subtle" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(CONTENT, 'min-w-(--radix-select-trigger-width)')}
        >
          <S.Viewport>
            {options.map((o) => (
              <S.Item key={o.value} value={o.value} disabled={o.disabled} className={cn(ITEM, 'pr-7')}>
                {o.icon}
                <S.ItemText>{o.label}</S.ItemText>
                <S.ItemIndicator className="absolute right-2">
                  <Check className="text-accent-text!" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
